import React, { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  ScrollErrorForm,
  ConfirmationCheckbox,
  ConfirmModal,
  Notification,
  PromoCode,
} from 'components'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { FlowActions } from '../components'
import * as Types from 'types'
import { isEmpty } from 'lodash'
import PaymentFields, { paymentSchema } from 'components/PaymentFields'
import { COMPANY_NAME, isPromoCodeEnabled } from 'config/portal'
import { E_SIGNATURE_CONFIRMATION_TEXT } from 'config/automatic-rollovers'
import { Variant as NotificationVariant } from 'components/Notification'
import { formatCurrency } from 'utils'

const propTypes = {
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  paymentEnabled: PropTypes.bool.isRequired,
  currentUser: Types.user.isRequired,
  paymentMethod: Types.paymentMethod,
  onConfirmDeletePayment: PropTypes.func.isRequired,
  isDeletingPaymentMethod: PropTypes.bool.isRequired,
  hasBalance: PropTypes.bool.isRequired,
  isMockedPayment: PropTypes.bool.isRequired,
  turnaroundTime: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  flashSuccessMessageHandler: PropTypes.func.isRequired,
  searchService: PropTypes.string.isRequired,
  numberOfSearches: PropTypes.number.isRequired,
  applyPromoCode: PropTypes.func.isRequired,
}

const defaultProps = {}

const formatAddressProperty = (property, stringSeparator = ' ') => {
  return property ? property + stringSeparator : ''
}

function formatBillingAddress({ name, address1, city, state, zipCode }) {
  const billingName = formatAddressProperty(name, ', ')
  const billingAddress = formatAddressProperty(address1)
  const billingCity = formatAddressProperty(city)
  const billingState = formatAddressProperty(state)
  const billingZip = formatAddressProperty(zipCode)
  return billingName + billingAddress + billingCity + billingState + billingZip
}

const getPaymentDetailsSchema = (
  paymentEnabled,
  hasBalance,
  hasPaymentMethod,
  paymentMethod
) => {
  if (!paymentEnabled) return Yup.object().nullable()

  if (hasBalance && !hasPaymentMethod) return paymentSchema
  if (!hasBalance && !hasPaymentMethod) return Yup.object().nullable()
  if (hasPaymentMethod && !hasBalance) return Yup.object().nullable()
  if (hasPaymentMethod && hasBalance && !!paymentMethod.creditCardLastFour) {
    return Yup.object().nullable()
  }
  if (hasPaymentMethod && hasBalance) return paymentSchema

  return Yup.object().nullable()
}

function ConfirmSubmissionForm({
  onSubmit,
  initialValues,
  paymentEnabled,
  currentUser,
  paymentMethod,
  onConfirmDeletePayment,
  isDeletingPaymentMethod,
  hasBalance,
  isMockedPayment,
  turnaroundTime,
  total,
  flashSuccessMessageHandler,
  numberOfSearches,
  searchService,
  applyPromoCode,
}) {
  const [showRemovePaymentModal, setShowRemovePaymentModal] = useState(false)
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false)
  const hasPaymentMethod = !isEmpty(paymentMethod)
  const baseTotalRef = useRef(null)

  const confirmSubmissionSchema = useMemo(() => {
    return Yup.object({
      confirm: Yup.boolean()
        .required('This checkbox must be checked in order to submit search')
        .oneOf(
          [true],
          'This checkbox must be checked in order to submit search'
        ),
      paymentDetails: Yup.lazy((value, options) => {
        const discountedTotal = options?.context?.discountedTotal ?? total ?? 0
        if (discountedTotal <= 0) return Yup.mixed().nullable()
        return getPaymentDetailsSchema(
          paymentEnabled,
          hasBalance,
          hasPaymentMethod,
          paymentMethod
        )
      }),
      confirmPayment: Yup.lazy((value, options) => {
        const discountedTotal = options?.context?.discountedTotal ?? total ?? 0
        if (discountedTotal <= 0) return Yup.mixed().nullable()
        if (paymentEnabled && hasBalance)
          return Yup.boolean().required('Required').oneOf([true], 'Required')
        return Yup.mixed().nullable()
      }),
    })
  }, [hasPaymentMethod, paymentMethod, paymentEnabled, hasBalance, total])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={confirmSubmissionSchema}
    >
      {({
        errors,
        touched,
        isSubmitting,
        values,
        setFieldValue,
        setFieldError,
      }) => {
        if (!values.promoApplied && !baseTotalRef.current) {
          baseTotalRef.current = total
        }

        const baseTotal = baseTotalRef.current ?? total ?? 0
        const discountedTotal = values.discountedTotal ?? total ?? 0
        const discountAmount = baseTotal - discountedTotal

        return (
          <ScrollErrorForm>
            <>
              {total > 0 && isPromoCodeEnabled() && (
                <PromoCode
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  totalDiscount={discountAmount}
                  flashSuccessMessageHandler={flashSuccessMessageHandler}
                  orderParams={{ searchService, numberOfSearches }}
                  applyPromoCode={applyPromoCode}
                  normalizeResponse={(response) => ({
                    isPromoCodeValid: response.isPromoCodeValid,
                    totalCost: response.totalOrderCost,
                  })}
                />
              )}
              <div className="total-payment-due-section">
                <h2>Total payment due: {formatCurrency(discountedTotal)}</h2>
              </div>
              <div className="turnaround-time-info">
                <dd>
                  <Notification
                    type={NotificationVariant.INFORMATIONAL}
                    isBadge={true}
                  >
                    Estimated turnaround time: {turnaroundTime}
                  </Notification>
                </dd>
              </div>
              {paymentEnabled && discountedTotal > 0 && (
                <>
                  <div className="form-header">
                    <div id="payment-details-header">
                      <h3 className="flow-section-headers">Payment details</h3>
                      {hasPaymentMethod && !isMockedPayment && (
                        <button
                          className="replace-payment-button"
                          aria-label="Delete payment method"
                          onClick={(e) => {
                            e.preventDefault()
                            setShowRemovePaymentModal(true)
                          }}
                        >
                          Replace
                        </button>
                      )}
                      {hasPaymentMethod && isMockedPayment && (
                        <button
                          className="replace-payment-button"
                          aria-label="Add payment method"
                          onClick={() => setShowAddPaymentModal(true)}
                        >
                          Add new card
                        </button>
                      )}
                    </div>
                    {touched.paymentDetails && errors.paymentDetails && (
                      <Notification type={NotificationVariant.ERROR}>
                        {errors.paymentDetails.cardData}
                      </Notification>
                    )}
                  </div>
                  {hasPaymentMethod &&
                    !isDeletingPaymentMethod &&
                    !isMockedPayment && (
                      <section className="confirm-order-details">
                        <dl>
                          <div>
                            <dt>Credit or debit card</dt>
                            <dd>{`${paymentMethod.cardType}: ${
                              paymentMethod.creditCardLastFour
                                ? paymentMethod.creditCardLastFour
                                : paymentMethod.cardLabel
                            }`}</dd>
                          </div>
                          <div className="full-row">
                            <dt>Billing address</dt>
                            <dd>{formatBillingAddress(paymentMethod)}</dd>
                          </div>
                        </dl>
                      </section>
                    )}
                  {showRemovePaymentModal && !isMockedPayment && (
                    <ConfirmModal
                      onClose={() => setShowRemovePaymentModal(false)}
                      onConfirm={() => {
                        setShowRemovePaymentModal(false)
                        onConfirmDeletePayment()
                      }}
                      confirmContent="Yes, Replace"
                      className="remove-payment-modal"
                    >
                      <h2>Replace payment details</h2>
                      <p>
                        Keep in mind you can only have one card on file.
                        Replacing it here will replace it across the platform.
                      </p>
                    </ConfirmModal>
                  )}
                  {showAddPaymentModal &&
                    hasPaymentMethod &&
                    isMockedPayment && (
                      <ConfirmModal
                        onClose={() => setShowAddPaymentModal(false)}
                        onConfirm={() => {
                          setShowAddPaymentModal(false)
                          onConfirmDeletePayment()
                        }}
                        confirmContent="Yes"
                        className="remove-payment-modal"
                      >
                        <h2>Add payment details</h2>
                      </ConfirmModal>
                    )}
                  {!hasPaymentMethod && !isDeletingPaymentMethod && (
                    <section className="confirm-order-details">
                      <div id="add-new-payment-form">
                        <PaymentFields sectionName="paymentDetails" />
                      </div>
                    </section>
                  )}
                  {!isMockedPayment && (
                    <div className="payment-confirmation">
                      <ConfirmationCheckbox
                        name="confirmPayment"
                        label={`I authorize ${COMPANY_NAME.long} to charge my credit card for the total amount of this purchase for search services. I understand that this charge will appear on my credit card statement as ${COMPANY_NAME.long}. I further certify that I am the authorized user of the credit card and that all information provided is true and accurate.`}
                      />
                    </div>
                  )}
                </>
              )}
              <div className="form-header" id="e-signature-header-container">
                <h3 className="flow-section-headers">E-Signature</h3>
              </div>
              <section className="confirm-order-details">
                <dl>
                  <div className="user-details">
                    <dt>First name</dt>
                    <dd>{currentUser.firstName}</dd>
                  </div>
                  <div className="user-details">
                    <dt>Last name</dt>
                    <dd>{currentUser.lastName}</dd>
                  </div>
                  <div className="user-details">
                    <dt>Title</dt>
                    <dd>{currentUser.title}</dd>
                  </div>
                </dl>
              </section>
              <div className="e-signature">
                <ConfirmationCheckbox
                  name="confirm"
                  label={E_SIGNATURE_CONFIRMATION_TEXT}
                />
              </div>
            </>
            <FlowActions isSubmitting={isSubmitting} submitContent="Submit" />
          </ScrollErrorForm>
        )
      }}
    </Formik>
  )
}

ConfirmSubmissionForm.propTypes = propTypes
ConfirmSubmissionForm.defaultProps = defaultProps

export default ConfirmSubmissionForm
